import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { waitForElasticConnection } from '../../../hooks/checkElasticConnection';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_SINGLE_STORE_DATA_NEW,
  GET_SINGLE_STORE_DATA_NEW_WITH_TIME,
  BASE_URL_NODE,
} from '../../../Constants/Config';
import { cacheDeziMeta } from '../../../utils';

const initialState = {
  loading: false,
  storesData: {},
  ElsticStoresData: {},
  filteredCategories: [],
  currentMerchantId: null,
  currentOrderMethod: null,
  storeRequestKey: null,
  activeStoreRequestKey: null,
  activeStoreRequestId: null,
  activeStoreTimeRequestKey: null,
  activeStoreTimeRequestId: null,
  merchantStoreAndCategoryInfo: {},
  merchantStoreAndCategoryInfoLoading: true,
  categoryRequestKey: null,
  error: '',
};

let isElasticConnected = false;

const normalizeStoreMerchantId = (merchantId) =>
  String(merchantId ?? '').trim().toUpperCase();

const normalizeStoreOrderMethod = (orderMethod) =>
  String(orderMethod ?? '').trim().toLowerCase();

export const createCheckoutStoreRequest = (data = {}) => ({
  merchant_id: data?.merchant_id,
  orderMethod: data?.orderMethod,
  distance: data?.distance ?? ' ',
  mainCart: true,
  ind_brn: data?.ind_brn ?? 'no',
});

export const createSingleStoreRequestKey = (data) => {
  const merchantId = normalizeStoreMerchantId(data?.merchant_id);
  const orderMethod = normalizeStoreOrderMethod(data?.orderMethod);
  if (!merchantId || !orderMethod) return '';

  const distance = String(data?.distance ?? ' ').trim();
  const independentBranch = String(data?.ind_brn ?? 'no')
    .trim()
    .toLowerCase();
  const context = data?.mainCart ? 'checkout' : 'storefront';
  return [merchantId, orderMethod, distance, independentBranch, context].join(
    '|',
  );
};

const isCategoryVisibleForOrderMethod = (category, orderMethod) => {
  const methodVisibility = String(category?.method_visibility ?? '0');

  if (methodVisibility === '3') {
    return false;
  }

  if (orderMethod === 'pickup') {
    // TEMP: empty-category check disabled - categories with a 0 product count
    // were being hidden even though the merchant expects them listed.
    // const hasPickupProducts =
    //   category.product_count_pick === '1' ||
    //   parseInt(category.product_count_pick, 10) >= 1;
    const allowsPickup = methodVisibility === '0' || methodVisibility === '1';
    return allowsPickup;
  }

  if (orderMethod === 'delivery') {
    // TEMP: empty-category check disabled - see note above.
    // const hasDeliveryProducts =
    //   category.product_count_del === '1' ||
    //   parseInt(category.product_count_del, 10) >= 1;
    const allowsDelivery = methodVisibility === '0' || methodVisibility === '2';
    return allowsDelivery;
  }

  return true;
};

// In-flight request maps to deduplicate concurrent requests by key
const inflightRequests = {
  elastic: new Map(),
  storeContext: new Map(),
};

const STORE_CONTEXT_CACHE_TTL_MS = 30 * 1000;
const STORE_CONTEXT_CACHE_MAX_ENTRIES = 50;
const storeContextCache = new Map();

const cacheStoreContext = (key, value) => {
  const now = Date.now();
  storeContextCache.forEach((entry, cacheKey) => {
    if (entry.expiresAt <= now) {
      storeContextCache.delete(cacheKey);
    }
  });

  if (storeContextCache.has(key)) {
    storeContextCache.delete(key);
  }
  while (storeContextCache.size >= STORE_CONTEXT_CACHE_MAX_ENTRIES) {
    const oldestKey = storeContextCache.keys().next().value;
    if (oldestKey === undefined) break;
    storeContextCache.delete(oldestKey);
  }

  storeContextCache.set(key, {
    value,
    expiresAt: now + STORE_CONTEXT_CACHE_TTL_MS,
  });
};

const getStoreContextKey = createSingleStoreRequestKey;

const requestStoreContext = (data = {}) => {
  const key = getStoreContextKey(data);
  const cached = storeContextCache.get(key);

  if (cached?.expiresAt > Date.now()) {
    return Promise.resolve(cached.value);
  }

  if (inflightRequests.storeContext.has(key)) {
    return inflightRequests.storeContext.get(key);
  }

  const payload = {
    merchant_id: String(data.merchant_id || '').trim(),
    orderMethod: String(data.orderMethod || '').trim().toLowerCase(),
    distance: data.distance ?? ' ',
    ind_brn: data.ind_brn ?? 'no',
    ...(data.mainCart ? { mainCart: true } : {}),
  };

  const request = axios
    .post(BASE_URL_NODE + GET_SINGLE_STORE_DATA_NEW, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response) => {
      if (response.data.status != 200) {
        throw new Error('Server error');
      }

      const value = response.data.result;
      cacheDeziMeta(
        value?.store_data?.merchant_id ?? data?.merchant_id,
        value?.store_data,
      );
      if (!data.mainCart) {
        cacheStoreContext(key, value);
      }
      return value;
    })
    .finally(() => {
      inflightRequests.storeContext.delete(key);
    });

  inflightRequests.storeContext.set(key, request);
  return request;
};

// AbortController to cancel previous requests
let abortControllers = {
  elastic: null,
};

// Generate pening , fulfilled and rejected action type
export const fetchSingleStoresData = createAsyncThunk(
  'singleStores/fetchSingleStoreData',
  async (data) => requestStoreContext(data),
);

export const fetchSingleStoresCategoryData = createAsyncThunk(
  'singleStores/fetchSingleStoresCategoryData',
  async (data) => requestStoreContext(data),
);

export const fetchSingleStoresElasticData = createAsyncThunk(
  'singleStores/fetchSingleStoresElasticData',
  async (data, { getState, rejectWithValue }) => {
    // Cancel previous request if exists
    if (abortControllers.elastic) {
      abortControllers.elastic.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllers.elastic = abortController;

    const key = `${data?.merchant_id || ''}|${data?.orderMethod || ''}|elastic`;

    // Clear any existing in-flight request for this key
    if (inflightRequests.elastic.has(key)) {
      inflightRequests.elastic.delete(key);
    }

    const promise = (async () => {
      try {
        isElasticConnected = await waitForElasticConnection(getState);
        const response = await axios.post(
          BASE_URL_NODE + GET_SINGLE_STORE_DATA_NEW,
          data,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: abortController.signal,
          },
        );
        if (response.data.status == 200) {
          return response.data.result;
        } else {
          throw new Error('Server error');
        }
      } catch (error) {
        // Don't throw error if request was cancelled
        if (
          axios.isCancel(error) ||
          error.name === 'CanceledError' ||
          error.name === 'AbortError'
        ) {
          return rejectWithValue({ cancelled: true });
        }
        throw new Error(error.message);
      } finally {
        inflightRequests.elastic.delete(key);
        // Clear abort controller if this is the current one
        if (abortControllers.elastic === abortController) {
          abortControllers.elastic = null;
        }
      }
    })();
    inflightRequests.elastic.set(key, promise);
    return await promise;
  },
);

export const fetchSingleStoresDataWithTime = createAsyncThunk(
  'singleStores/fetchSingleStoresDataWithTime',
  async (data) => {
    try {
      const response = await axios.post(
        BASE_URL_SANDBOX + GET_SINGLE_STORE_DATA_NEW_WITH_TIME,
        data,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );

      if (response.data.status === 200) {
        return response.data.result;
      } else throw new Error(`Server error`);
    } catch (error) {
      new Error(error.message);
    }
  },
);

// used in main single store old...
export const fetchSingleStoresDataOld = createAsyncThunk(
  'singleStores/fetchSingleStoreData',
  async (data) => {
    try {
      const response = await axios.post(
        BASE_URL_NODE + GET_SINGLE_STORE_DATA_NEW,
        data,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      if (response.data.status == 200) {
        return response.data.result;
      } else throw new Error(`Server error`);
    } catch (error) {
      new Error(error.message);
    }
  },
);

const singleStoresSlice = createSlice({
  name: 'singleStores',
  initialState,
  reducers: {
    // Set filtered categories based on order method
    setFilteredCategories: (state, action) => {
      const { categories, orderMethod, merchantId } = action.payload;

      if (!categories || !Array.isArray(categories)) {
        state.filteredCategories = [];
        return;
      }

      // Filter categories based on order method
      const filtered = categories.filter((category) =>
        isCategoryVisibleForOrderMethod(category, orderMethod),
      );

      state.filteredCategories = filtered;
      state.currentMerchantId = merchantId;
      state.currentOrderMethod = orderMethod;
    },
    // Clear filtered categories
    clearFilteredCategories: (state) => {
      state.filteredCategories = [];
      state.currentMerchantId = null;
      state.currentOrderMethod = null;
    },
    // Clear all merchant data
    clearMerchantData: (state) => {
      state.storesData = {};
      state.ElsticStoresData = {};
      state.filteredCategories = [];
      state.currentMerchantId = null;
      state.currentOrderMethod = null;
      state.storeRequestKey = null;
      state.activeStoreRequestKey = null;
      state.activeStoreRequestId = null;
      state.activeStoreTimeRequestKey = null;
      state.activeStoreTimeRequestId = null;
      state.categoryRequestKey = null;
      state.merchantStoreAndCategoryInfo = {};
      state.merchantStoreAndCategoryInfoLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSingleStoresData.pending, (state, action) => {
      const requestKey = createSingleStoreRequestKey(action.meta?.arg);
      if (state.storeRequestKey !== requestKey) {
        state.storesData = {};
        state.storeRequestKey = null;
      }
      state.loading = true;
      state.error = '';
      state.activeStoreRequestKey = requestKey;
      state.activeStoreRequestId = action.meta?.requestId || null;
    });
    builder.addCase(fetchSingleStoresData.fulfilled, (state, action) => {
      const requestKey = createSingleStoreRequestKey(action.meta?.arg);
      if (
        state.activeStoreRequestId !== action.meta?.requestId ||
        state.activeStoreRequestKey !== requestKey
      ) {
        return;
      }

      const responseMerchantId = normalizeStoreMerchantId(
        action.payload?.store_data?.merchant_id,
      );
      const requestedMerchantId = normalizeStoreMerchantId(
        action.meta.arg?.merchant_id,
      );
      state.loading = false;
      state.activeStoreRequestId = null;
      state.activeStoreRequestKey = null;

      if (
        !requestKey ||
        !responseMerchantId ||
        responseMerchantId !== requestedMerchantId
      ) {
        state.storesData = {};
        state.storeRequestKey = null;
        state.error = 'Store response did not match the requested merchant';
        return;
      }

      //   if (isElasticConnected && action.payload.categories) {
      //     state.storesData = {
      //       ...state.storesData,
      //       store_data: action.payload.store_data,
      //       categories: state?.storesData?.categories
      //         ? [...state.storesData.categories]
      //         : [],
      //     };
      //   } else {
      //     state.storesData = {
      //       ...state.storesData,
      //       ...action.payload,
      //     };
      //   }

      state.storesData = {
        // ...state.storesData,
        ...action.payload,
      };
      state.storeRequestKey = requestKey;
      state.error = '';
    });

    builder.addCase(fetchSingleStoresData.rejected, (state, action) => {
      const requestKey = createSingleStoreRequestKey(action.meta?.arg);
      if (
        state.activeStoreRequestId !== action.meta?.requestId ||
        state.activeStoreRequestKey !== requestKey
      ) {
        return;
      }

      state.loading = false;
      state.storesData = {};
      state.storeRequestKey = null;
      state.activeStoreRequestKey = null;
      state.activeStoreRequestId = null;
      state.error = action.error.message;
    });

    builder.addCase(fetchSingleStoresCategoryData.pending, (state, action) => {
      const requestKey = getStoreContextKey(action.meta.arg);
      if (state.categoryRequestKey !== requestKey) {
        state.merchantStoreAndCategoryInfo = {};
        state.categoryRequestKey = requestKey;
      }
      state.merchantStoreAndCategoryInfoLoading = true;
    });
    builder.addCase(
      fetchSingleStoresCategoryData.fulfilled,
      (state, action) => {
        const requestKey = getStoreContextKey(action.meta.arg);
        if (state.categoryRequestKey !== requestKey) return;

        state.merchantStoreAndCategoryInfoLoading = false;
        state.merchantStoreAndCategoryInfo = action.payload;
        // state.error = '';
      },
    );

    builder.addCase(fetchSingleStoresCategoryData.rejected, (state, action) => {
      const requestKey = getStoreContextKey(action.meta.arg);
      if (state.categoryRequestKey !== requestKey) return;

      state.merchantStoreAndCategoryInfoLoading = false;
      state.merchantStoreAndCategoryInfo = {};
      // state.error = action.error.message;
    });

    builder.addCase(fetchSingleStoresElasticData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchSingleStoresElasticData.fulfilled, (state, action) => {
      state.loading = false;
      state.ElsticStoresData = {
        ...action.payload,
      };
      state.error = '';

      // Auto-update filtered categories if we have the necessary data
      if (
        action.payload?.categories &&
        state.currentOrderMethod &&
        state.currentMerchantId
      ) {
        const filtered = action.payload.categories.filter((category) =>
          isCategoryVisibleForOrderMethod(category, state.currentOrderMethod),
        );
        state.filteredCategories = filtered;
      }
    });

    builder.addCase(fetchSingleStoresElasticData.rejected, (state, action) => {
      // Don't update state if request was cancelled
      if (action.payload?.cancelled) {
        state.loading = false;
        return;
      }
      state.loading = false;
      state.ElsticStoresData = {};
      state.error = action.error.message;
    });

    builder.addCase(fetchSingleStoresDataWithTime.pending, (state, action) => {
      state.loading = true;
      state.error = '';
      state.activeStoreTimeRequestKey = createSingleStoreRequestKey(
        action.meta?.arg,
      );
      state.activeStoreTimeRequestId = action.meta?.requestId || null;
    });

    builder.addCase(
      fetchSingleStoresDataWithTime.fulfilled,
      (state, action) => {
        const requestKey = createSingleStoreRequestKey(action.meta?.arg);
        if (
          state.activeStoreTimeRequestId !== action.meta?.requestId ||
          state.activeStoreTimeRequestKey !== requestKey
        ) {
          return;
        }

        const requestedMerchantId = normalizeStoreMerchantId(
          action.meta.arg?.merchant_id,
        );
        const currentMerchantId = normalizeStoreMerchantId(
          state.storesData?.store_data?.merchant_id,
        );
        state.loading = false;
        state.activeStoreTimeRequestId = null;
        state.activeStoreTimeRequestKey = null;
        if (
          action?.payload?.store_data &&
          requestedMerchantId &&
          requestedMerchantId === currentMerchantId
        ) {
          state.storesData = {
            ...state.storesData,
            store_data: {
              ...state?.storesData?.store_data,
              ...action?.payload?.store_data,
            },
          };
        }
        // else {
        //   state.storesData = {
        //     ...state.storesData,
        //   };
        // }

        state.error = '';
      },
    );

    builder.addCase(fetchSingleStoresDataWithTime.rejected, (state, action) => {
      const requestKey = createSingleStoreRequestKey(action.meta?.arg);
      if (
        state.activeStoreTimeRequestId !== action.meta?.requestId ||
        state.activeStoreTimeRequestKey !== requestKey
      ) {
        return;
      }

      state.loading = false;
      state.activeStoreTimeRequestId = null;
      state.activeStoreTimeRequestKey = null;
      state.error = action.error.message;
    });
  },
});

// Export actions
export const {
  setFilteredCategories,
  clearFilteredCategories,
  clearMerchantData,
} = singleStoresSlice.actions;

export default singleStoresSlice.reducer;
